import { entityTableLabelMap, entityTypeMap } from 'common/table-viewer/utils';
import {
  BuildMethodsType,
  CommonMetadataType,
  DrillingUnitType, EntityMetadataType, EntityType, metadataLabelMap, MetadataType, ReferencePositionType, TemperatureDefinitionNameType, WellConfigurationType, WellStatusType,
} from 'common/enums/entity-enums';
import {
  ArchiveQueryCondition, DefaultQueryResult, ICondition, IConditionGroup, IQuery, QueryLogicalOperator,
  TQueryResultTable,
} from 'common/interfaces/query';
import { KnowEdmTableData as WellboreWellheadPositionParameters } from 'common/services/configs/locations/table-enums';
import { KnowEdmTableData as projectDataParameters } from 'common/services/configs/project/table-enums';
import { KnowEdmTableData as TemperatureDefinitionParameters } from 'common/services/configs/temperatures/table-enums';
import { KnowTableData as TrajectoryPlanParameters } from 'common/services/configs/trajectories/table-enums';
import { EDM_NODES_DATA, EdmNode, getAllEdmObjects } from 'common/constants/edm-nodes-data';
import { OwnerType } from 'common/enums/owner-enums';
import type { TFunction } from 'i18next';
import translations from 'client/internationalization/translation-map.json';

export const QUERY_TEMPLATE_OWNER_TYPE_LABEL_MAP: Record<OwnerType, string> = {
  [OwnerType.OLIASOFT]: translations.query_ownerTypeOliasoft,
  [OwnerType.USER]: translations.query_ownerTypeUser,
  [OwnerType.CUSTOMER]: translations.query_ownerTypeCompany,
};

export interface IQueryTemplateOption {
  label: string;
  value?: string;
  type?: 'Heading';
}

export const getQueryTemplateOptions = (queries: IQuery[], t: TFunction): IQueryTemplateOption[] => (
  Object.values(OwnerType).flatMap((ownerType) => {
    const ownerQueries = queries.filter(query => query.ownerType === ownerType);
    return ownerQueries.length
      ? [
        { label: t(QUERY_TEMPLATE_OWNER_TYPE_LABEL_MAP[ownerType]), type: 'Heading' as const },
        ...ownerQueries.map(query => ({ label: query.queryName, value: query.queryid })),
      ]
      : [];
  })
);

export const conditionLabelMap: Record<ArchiveQueryCondition, string> = {
  [ArchiveQueryCondition.EQ]: '=',
  [ArchiveQueryCondition.NE]: '≠',
  [ArchiveQueryCondition.GT]: '>',
  [ArchiveQueryCondition.GTE]: '≥',
  [ArchiveQueryCondition.LT]: '<',
  [ArchiveQueryCondition.LTE]: '≤',
};

const EQUALITY_CONDITIONS = [
  ArchiveQueryCondition.EQ,
  ArchiveQueryCondition.NE
];
const COMPARISON_CONDITIONS = [
  ArchiveQueryCondition.EQ,
  ArchiveQueryCondition.GT,
  ArchiveQueryCondition.GTE,
  ArchiveQueryCondition.LT,
  ArchiveQueryCondition.LTE,
];

export const getAllowedConditions = (entityType: EntityType, entityPropertyName: string): ArchiveQueryCondition[] => (
  getEntityParameterEnums(entityType, entityPropertyName) ? EQUALITY_CONDITIONS : COMPARISON_CONDITIONS
);

export const getEntityPropertyOptions = (entityType: EntityType) => {
  const entityProps = entityTypeMap.get(entityType);
  return entityProps ? ([...entityProps.values()].map(prop => ({
    label: prop.label,
    value: prop.key,
  }))) : [];
};

export const getEntityParamLabel = (entityType: EntityType, param: string) => {
  const entityProps = entityTypeMap.get(entityType);
  return entityProps ? entityProps.get(param)?.label : param;
};

export const getEntityParameterEnums = (entityType: EntityType, param: string) => {
  switch (entityType) {
    case EntityType.ProjectData:
      if (param === projectDataParameters.WellConfiguration) return Object.values(WellConfigurationType);
      if (param === projectDataParameters.DrillingUnit) return Object.values(DrillingUnitType);
      if (param === projectDataParameters.WellStatus) return Object.values(WellStatusType);
      break;
    case EntityType.WellboreWellheadPosition:
      if (param === WellboreWellheadPositionParameters.ReferencePositionType) return Object.values(ReferencePositionType);
      break;
    case EntityType.TemperatureDefinition:
      if (param === TemperatureDefinitionParameters.GeothermalTempDefinitionName) return Object.values(TemperatureDefinitionNameType);
      break;
    case EntityType.TrajectoryPlan:
      if (param === TrajectoryPlanParameters.BuildMethod) return Object.values(BuildMethodsType);
      break;
    default:
      break;
  }
  return false;
};

export const getEntityParameterEnumOptions = (entityType: EntityType, param: string) => {
  const values = getEntityParameterEnums(entityType, param);
  return values ? (values.map(value => ({
    label: value,
    value,
  }))) : [];
};

export const getEntityMetadataOptions = (entityType: EntityType) => {
  const entityObject = getAllEdmObjects(Object.values(EDM_NODES_DATA) as EdmNode[])
    .find(object => String(object.type) === String(entityType));
  const entityMetadata = Object.keys((entityObject as any)?.metadata || {}) as EntityMetadataType[];
  const commonMetadataOption = Object.values(CommonMetadataType).map((value) => ({
    label: metadataLabelMap[value],
    value: `${MetadataType.Common}-${value}`,
  }));
  const entityMetadataOptions = entityMetadata.map((value) => ({
    label: metadataLabelMap[value],
    value: `${MetadataType.Entity}-${value}`,
  }));
  return [...commonMetadataOption, ...entityMetadataOptions];
};

export const defaultQuery = ({ companyId, queryName = 'New query' }: { companyId: string, queryName?: string }) => ({
  owner: '',
  ownerType: OwnerType.USER,
  companyid: companyId,
  queryName,
  archiveid: companyId,
  groupBy: '',
  dataType: [`wellConfiguration-${EntityType.ProjectData}`],
  conditionGroups: [],
  groupsOperator: QueryLogicalOperator.And,
  resultConfig: {
    hierarchy: [],
    filters: {},
  },
});

export const emptyCondition = {
  entityType: '',
  entityPropertyName: '',
  condition: '',
  conditionValue: null,
};

export const defaultCondition = {
  entityType: EntityType.ProjectData,
  entityPropertyName: 'wellConfiguration',
  condition: ArchiveQueryCondition.EQ,
  conditionValue: null,
} as ICondition;

export const defaultConditionGroup: IConditionGroup = {
  operator: QueryLogicalOperator.And,
  conditions: [defaultCondition],
};

export const emptyConditionGroup = {
  operator: QueryLogicalOperator.And,
  conditions: [emptyCondition],
} as unknown as IConditionGroup;

export const getGroupedConditions = (conditionGroups: IConditionGroup[]) => conditionGroups.flatMap(({ conditions }) => conditions);

export const allConditionsFilled = (arr: {}[]) => {
  return arr.every(obj => Object.values(obj).every(value => value !== null && value !== '' && value !== undefined));
};

export const getDataTypeOptions = () => {
  const dataTypeOptions = [];
  for (const [entityType, value] of entityTypeMap) {
    dataTypeOptions.push({
      label: entityTableLabelMap[entityType] || entityType,
      type: 'Heading',
    });
    for (const [entityPropKey, entityPropValue] of value) {
      dataTypeOptions.push({
        label: entityPropValue.label || entityPropKey,
        value: `${entityPropValue.key}-${entityType}`,
        entityType,
      });
    }
  }
  return dataTypeOptions;
};

export const getResultEntityPath = (companyId: string | undefined, archiveId: string | undefined, designid: string) => {
  return `/${companyId}/explore?activeArhiveId=${archiveId}&activeEntityId=${designid}&activeEntityType=design`;
};

export const filterDefaultQueryResult = (dataRows: DefaultQueryResult['data'], filters: { [key: string]: string } = {}): DefaultQueryResult['data'] => dataRows.reduce((acc: DefaultQueryResult['data'], group) => {
  if (!filters?.[group.entityType] || group?.name.toString().includes(filters[group.entityType])) {
    const filteredGropsItems = group.objects.filter((object) => Object.keys(filters).every(key => (filters[key] === '' ? true : object?.[key]?.name.toString().includes(filters[key]))));
    if (filteredGropsItems.length === 0) {
      return acc;
    }
    const updatedGroup = { ...group, objects: filteredGropsItems };
    return [...acc, updatedGroup];
  }
  return acc;
}, [] as DefaultQueryResult['data']);

export const getObjectValueRows = (data: unknown): Record<string, any>[] => (Array.isArray(data) ? data.slice(1) : []);

export const filterQueryResult = (dataRows: TQueryResultTable['data'], hierarchy: string[], filters: { [key: string]: string } = {}): TQueryResultTable['data'] => dataRows.reduce((acc, group) => {
  if (!filters?.[group.entityType] || group?.name.toString().includes(filters[group.entityType])) {
    const filteredGropsItems = group.objects.filter((object) => Object.keys(filters).every((key) => {
      const isHierachyFilter = hierarchy.includes(key);
      if (isHierachyFilter) {
        return Object.values(object)?.every(o => o?.metadata.hierarchyData?.[key]?.name.toString().includes(filters[key]));
      }
      const [entity, prop] = key.split('-');
      return object?.[entity] && Array.isArray(object?.[entity]?.data) ? getObjectValueRows(object?.[entity]?.data).some((d) => (d[prop] ?? '')?.toString().includes(filters[key])) : true;
    }));
    if (filteredGropsItems.length === 0) {
      return acc;
    }
    const updatedGroup = { ...group, objects: filteredGropsItems };
    return [...acc, updatedGroup];
  }
  return acc;
}, [] as TQueryResultTable['data']);

export const getConditionDataTypeKey = ({ entityPropertyName, entityType }: Pick<ICondition, 'entityPropertyName' | 'entityType'>) => `${entityPropertyName}-${entityType}`;

export const replaceConditionDataType = (
  dataType: string[] = [],
  oldKey: string,
  newKey: string | undefined,
  otherConditions: ICondition[],
) => {
  const isOldKeyStillUsed = otherConditions.some(condition => getConditionDataTypeKey(condition) === oldKey);
  const withoutOldKey = isOldKeyStillUsed ? dataType : dataType.filter(key => key !== oldKey);
  const isNewKeyValid = !!newKey && getDataTypeOptions().some(option => option.value === newKey);
  return isNewKeyValid && !withoutOldKey.includes(newKey) ? [...withoutOldKey, newKey] : withoutOldKey;
};